import React, { ReactElement, useCallback, useState } from "react";

import styled from "styled-components";

import { TRANSITION } from "./tokens";
import {
  CollapseExpandSingleIcon,
  HelpIcon,
  MessageEmptyIcon,
  NoteIcon,
  PlayCircleOutlineIcon,
  StarOutlineIcon,
} from "../../components/Icons";
import { List, ListItem, ListSeparator } from "../../components/List";
import { Menu } from "../../components/Menu";
import { Tooltip } from "../../components/Tooltip";
import { Body2 } from "../../components/Typography";

/* ------------------------------------------------------------------ */
/* Help & Support menu (mirrors MainMenu/HelpAndSupportMenu.js)        */
/* ------------------------------------------------------------------ */

/**
 * The "Help & Support" entry that sits at the very bottom of the main menu.
 * Clicking it opens a popover (Help Docs / YouTube Channel / Live Support /
 * New Features), matching the real MainMenu/HelpAndSupportMenu.js. It is a
 * shared building block because every Product Messaging concept re-uses this
 * same entry point.
 *
 * The label + caret carry the `menu-item-text` class so the shell's existing
 * collapse rules hide them automatically when the menu is collapsed (state or
 * <1280px media query). Clicks are intentionally no-ops in the mock.
 */

const StyledHelpTarget = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 40px;
  padding: 0 4px 0 12px;
  margin: 0 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color ${TRANSITION};

  .help-icon {
    flex-shrink: 0;
    fill: var(--color-theme-700);
    opacity: 0.7;
  }

  .help-label {
    margin-left: 12px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .help-caret {
    margin-left: auto;
    flex-shrink: 0;
    fill: var(--color-theme-700);
    opacity: 0.7;
    transition: transform ${TRANSITION};
    transform: rotate(180deg);
  }

  &:hover {
    background-color: var(--color-theme-300);

    .help-icon,
    .help-caret {
      fill: var(--color-theme-900);
      opacity: 1;
    }

    .help-label {
      color: var(--color-theme-900);
    }
  }

  &.open {
    background-color: var(--color-secondary-200);

    .help-icon,
    .help-caret {
      fill: var(--color-secondary);
      opacity: 1;
    }

    .help-label {
      color: var(--color-secondary);
    }

    .help-caret {
      transform: rotate(0deg);
    }
  }
`;

/* The popover body. ListItem gives leading icons an 8px gap by default; the
   real menu uses 12px (tw-mr-3), so bump it here. */
const StyledHelpMenu = styled.div`
  width: 216px;

  .c-list {
    padding: 8px 0;
  }

  .c-list-item > svg:first-child {
    margin-right: 12px;
  }
`;

export interface HelpAndSupportMenuProps {
  /** Disable the hover tooltip (used inside the mobile menu). */
  mobile?: boolean;
}

export const HelpAndSupportMenu = ({
  mobile = false,
}: HelpAndSupportMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <Menu
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      position="top-start"
      target={
        <Tooltip title="Help & Support" placement="right" disable={mobile}>
          <StyledHelpTarget
            className={open ? "open" : undefined}
            data-testid="help-support-menu"
            onClick={handleOpen}
          >
            <HelpIcon className="help-icon" />
            <Body2
              weight="medium"
              color="secondary"
              whitespace="no-wrap"
              className="help-label menu-item-text"
            >
              Help &amp; Support
            </Body2>
            <CollapseExpandSingleIcon className="help-caret menu-item-text" />
          </StyledHelpTarget>
        </Tooltip>
      }
    >
      <StyledHelpMenu>
        <List tabIndex={-1}>
          <ListItem onClick={handleClose}>
            <NoteIcon />
            Help Docs
          </ListItem>
          <ListItem onClick={handleClose}>
            <PlayCircleOutlineIcon />
            YouTube Channel
          </ListItem>
          <ListItem onClick={handleClose}>
            <MessageEmptyIcon />
            Live Support
          </ListItem>
          <ListSeparator />
          <ListItem onClick={handleClose}>
            <StarOutlineIcon />
            New Features
          </ListItem>
        </List>
      </StyledHelpMenu>
    </Menu>
  );
};

HelpAndSupportMenu.displayName = "HelpAndSupportMenu";
